import React, { useState, useRef } from "react";
import { Building2, User, Plus, Loader2 } from "lucide-react";
import { DatePicker, AutoComplete, Spin } from "antd";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import {
  getCompany,
  getCustomersInt,
  createCreditAccount,
  updateCreditAccStatus,
} from "../../../Store/Apis/ReservationApi";
const ACCOUNT_PAYMENT_TYPE_ID = 10001; // constant ID for "Account Payment"

const AddAccount = () => {
  const [clientType, setClientType] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false); // edit mode if existing account has payment_Terms
  const [accountStatus, setAccountStatus] = useState(""); // Active | On hold | ""
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [showPendingMessage, setShowPendingMessage] = useState(false);

  // Single source of truth for empty form
  const emptyForm = {
    companyName: "",
    companyAddress: "",
    companyPhoneNumber: "",
    companyEmailAddress: "",
    companyID: "",
    customerID: "",
    customerName: "",
    phoneNumber: "",
    address: "",
    creditStartDate: "",
    paymentTerms: "",
    paymentTypeID: "",
    creditLimit: "",
    notes: "",
    payment_Terms: "", // raw field from API (if any) used to detect edit mode
  };

  // Use emptyForm for initial state
  const [form, setForm] = useState(emptyForm);

  // Autocomplete state
  const [searchValue, setSearchValue] = useState("");
  const [options, setOptions] = useState([]); // AutoComplete options
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimeout = useRef(null);

  // Debounced search handler
  const triggerSearch = (value) => {
    if (!clientType) return;
    if (value.length < 2) {
      setOptions([]);
      return;
    }
    setSearchLoading(true);
    const run = async () => {
      try {
        if (clientType === "company") {
          const companies = await getCompany({
            companyName: value,
            outletID: "4",
          });
          if (companies) {
            setOptions(
              companies.map((c) => ({
                value: c.companyName,
                label: `${c.companyName} | ${c.companyEmailAddress || ""}`,
                raw: c,
              }))
            );
          }
        } else if (clientType === "customer") {
          const customers = await getCustomersInt({ keyword: value });
          if (customers) {
            setOptions(
              customers.map((c) => ({
                value: c.customerName || c.name,
                label: `${c.customerName || c.name} | ${
                  c.customerEmail || c.email || ""
                }`,
                raw: c,
              }))
            );
          }
        }
      } catch (e) {
        console.error("Search error", e);
      } finally {
        setSearchLoading(false);
      }
    };
    run();
  };

  const handleSearch = (value) => {
    setSearchValue(value);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => triggerSearch(value), 350);
  };

  const handleSelect = (value, option) => {
    const data = option.raw;
    // Normalise field names coming from API (defensive)
    const rawPaymentTerms =
      data.payment_Terms || data.paymentTerms || data.payment_terms || "";
    const rawStatus =
      data.creditAccStatus ||
      data.status ||
      data.accountStatus ||
      data.creditStatus ||
      "";
    const rawLimit =
      data.limit ?? data.creditLimit ?? data.credit_Limit ?? data.credit_limit;
    const rawDateStr =
      data.accPayDate ||
      data.AccPayDate ||
      data.acc_pay_date ||
      data.creditStartDate ||
      data.credit_start_date ||
      "";

    // Parse date to YYYY-MM-DD if present
    let parsedDate = "";
    if (rawDateStr) {
      try {
        const d = new Date(rawDateStr);
        if (!isNaN(d)) {
          const yyyy = d.getFullYear();
          const mm = String(d.getMonth() + 1).padStart(2, "0");
          const dd = String(d.getDate()).padStart(2, "0");
          parsedDate = `${yyyy}-${mm}-${dd}`;
        }
      } catch (_) {
        parsedDate = "";
      }
    }

    const limitVal =
      rawLimit !== undefined && rawLimit !== null ? rawLimit : "";

    if (clientType === "company") {
      setForm((prev) => ({
        ...prev,
        companyName: data.companyName || value,
        companyID: data.companyID || data.id || "",
        companyAddress: data.companyAddress || "",
        companyPhoneNumber: data.companyPhoneNumber || data.phoneNumber || "",
        companyEmailAddress:
          data.companyEmailAddress || data.companyEmail || data.email || "",
        payment_Terms: rawPaymentTerms,
        paymentTerms: rawPaymentTerms || prev.paymentTerms,
        creditStartDate: parsedDate || prev.creditStartDate,
        creditLimit: limitVal !== "" ? limitVal : prev.creditLimit,
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        customerName: data.customerName || data.name || value,
        customerID: data.customerID || data.id || "",
        phoneNumber: data.phoneNumber || data.customerPhoneNumber || "",
        address: data.address || data.customerAddress || "",
        payment_Terms: rawPaymentTerms,
        paymentTerms: rawPaymentTerms || prev.paymentTerms,
        creditStartDate: parsedDate || prev.creditStartDate,
        creditLimit: limitVal !== "" ? limitVal : prev.creditLimit,
      }));
    }
    setIsEditing(!!rawPaymentTerms);
    setAccountStatus(rawStatus);
    // Debug (can be removed later)
    console.log("Selected record (normalized)", {
      rawPaymentTerms,
      rawStatus,
      parsedDate,
      limitVal,
      original: data,
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));

    // Check credit limit for pending approval message (applies to both create and update)
    if (name === "creditLimit") {
      const creditAmount = parseFloat(value) || 0;
      setShowPendingMessage(creditAmount > 10000);
    }
  };

  const resetForm = (resetClientType = false) => {
    setForm(emptyForm);
    setSearchValue("");
    setOptions([]);
    setIsEditing(false);
    setAccountStatus("");
    setShowPendingMessage(false);
    if (resetClientType) setClientType("");
  };

  const handleClientTypeChange = (type) => {
    setClientType(type);
    // Clear form fields but keep selected type
    resetForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const selectedCustomerID =
      clientType === "company" ? form.companyID : form.customerID;

    // Always use today's date for new accounts; preserve existing date in edit mode
    const creditStartDateToSend = isEditing
      ? form.creditStartDate || dayjs().format("YYYY-MM-DD")
      : dayjs().format("YYYY-MM-DD");

    // Determine if credit limit exceeds 10,000 - CRITICAL: This applies to both create AND update
    const creditAmount = form.creditLimit ? Number(form.creditLimit) : 0;
    const exceedLimit = creditAmount > 10000 ? "Yes" : "No";

    const payload = {
      customerID: selectedCustomerID ? Number(selectedCustomerID) : 0,
      paymentTypeID: ACCOUNT_PAYMENT_TYPE_ID,
      paymentTerms: form.paymentTerms,
      creditLimit: creditAmount,
      notes: form.notes || "",
      creditStartDate: creditStartDateToSend,
      exceed_Limit: exceedLimit, // Always set this based on current credit limit
    };

    try {
      setSubmitting(true);
      const res = await createCreditAccount(payload);
      if (res?.status === 200) {
        const successMessage = isEditing
          ? "Credit account updated successfully!"
          : "Credit account created successfully!";
        toast.success(successMessage);

        // Show additional message if limit was exceeded
        if (exceedLimit === "Yes") {
          toast(
            "Credit limit exceeds R 10,000 - account flagged for approval.",
            {
              icon: "⚠️",
              duration: 4000,
            }
          );
        }

        resetForm(true); // full reset including client type
      } else {
        const errorMessage = isEditing
          ? "Error updating credit account"
          : "Error creating credit account";
        toast.error(errorMessage);
      }
    } catch (err) {
      const actionText = isEditing ? "Update" : "Create";
      toast.error(
        `${actionText} credit account failed${
          err?.message ? ": " + err.message : ""
        }`
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async () => {
    const selectedCustomerID =
      clientType === "company" ? form.companyID : form.customerID;
    if (!selectedCustomerID) return;
    const current = (accountStatus || "Active").toLowerCase();
    const nextStatus = current === "on hold" ? "Active" : "on hold";
    try {
      setStatusUpdating(true);
      // optimistic UI
      setAccountStatus(nextStatus);
      const res = await updateCreditAccStatus({
        clientId: Number(selectedCustomerID),
        newStatus: nextStatus,
      });
      if (!(res?.status >= 200 && res?.status < 300)) {
        // revert on failure
        setAccountStatus(accountStatus);
        toast.error(res?.message || "Failed to update status");
      } else {
        toast.success(
          nextStatus === "on hold"
            ? "Account put on hold"
            : "Account set to Active"
        );
      }
    } catch (e) {
      setAccountStatus(accountStatus);
      toast.error("Failed to update status");
    } finally {
      setStatusUpdating(false);
    }
  };

  return (
    <div className="card p-4">
      <h5 className="mb-4">
        {isEditing ? "Edit Credit Account" : "Add Credit Account"}
      </h5>

      {/* Client Type Selection */}
      <div className="mb-4">
        <p className="mb-3">Select the type of client you want to add:</p>
        <div className="row g-3">
          <div className="col-md-6">
            <div
              className={`card border cursor-pointer ${
                clientType === "company"
                  ? "border-primary bg-primary bg-opacity-10"
                  : "border-secondary"
              }`}
              style={{ cursor: "pointer" }}
              onClick={() => handleClientTypeChange("company")}
            >
              <div className="card-body text-center">
                <Building2
                  size={32}
                  className={
                    clientType === "company" ? "text-primary" : "text-muted"
                  }
                />
                <h6
                  className={`mt-2 mb-1 ${
                    clientType === "company" ? "text-primary" : ""
                  }`}
                >
                  Company Client
                </h6>
                <small
                  className={
                    clientType === "company" ? "text-primary" : "text-muted"
                  }
                >
                  For business organizations
                </small>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div
              className={`card border cursor-pointer ${
                clientType === "customer" ? "text-white" : "border-secondary"
              }`}
              style={
                clientType === "customer"
                  ? {
                      cursor: "pointer",
                      backgroundColor: "#cc9414",
                      borderColor: "#cc9414",
                      color: "#ffffff",
                    }
                  : { cursor: "pointer" }
              }
              onClick={() => handleClientTypeChange("customer")}
            >
              <div className="card-body text-center">
                <User
                  size={32}
                  className={
                    clientType === "customer" ? "text-white" : "text-muted"
                  }
                />
                <h6
                  className={`mt-2 mb-1 ${
                    clientType === "customer" ? "text-white" : ""
                  }`}
                >
                  Individual Customer
                </h6>
                <small
                  className={
                    clientType === "customer"
                      ? "text-white opacity-75"
                      : "text-muted"
                  }
                >
                  For individual clients
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      {clientType && (
        <form onSubmit={handleSubmit} className="row g-3">
          {/* Account Status Section (for existing or selected client) */}
          {(form.companyID || form.customerID) && (
            <div className="col-12 d-flex align-items-center justify-content-between p-3 border rounded">
              <div className="d-flex align-items-center gap-2">
                <span className="fw-semibold">Account Status:</span>
                <span
                  className={`badge ${
                    (accountStatus || "Active").toLowerCase() === "on hold"
                      ? "bg-warning text-dark"
                      : "bg-success"
                  }`}
                >
                  {accountStatus || "Active"}
                </span>
              </div>
              <button
                type="button"
                className="btn btn-outline-warning btn-sm"
                onClick={handleToggleStatus}
                disabled={statusUpdating}
                title={
                  (accountStatus || "Active").toLowerCase() === "on hold"
                    ? "Set Active"
                    : "Put On Hold"
                }
              >
                {statusUpdating
                  ? "Updating..."
                  : (accountStatus || "Active").toLowerCase() === "on hold"
                  ? "Set Active"
                  : "Put On Hold"}
              </button>
            </div>
          )}
          {/* Client Information Section */}
          <div className="col-12">
            <h6 className="border-bottom pb-2 mb-3">
              {clientType === "company"
                ? "Company Information"
                : "Customer Information"}
            </h6>
          </div>

          {clientType === "company" ? (
            <>
              <div className="col-12">
                <label className="form-label">Search Company</label>
                <AutoComplete
                  value={searchValue}
                  options={options}
                  onSearch={handleSearch}
                  onSelect={handleSelect}
                  placeholder="Type company name..."
                  className="w-100"
                  allowClear
                  notFoundContent={searchLoading ? <Spin size="small" /> : null}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Company Name</label>
                <input
                  name="companyName"
                  value={form.companyName}
                  onChange={handleChange}
                  className="form-control"
                  disabled
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Company ID</label>
                <input
                  name="companyID"
                  value={form.companyID}
                  onChange={handleChange}
                  className="form-control"
                  disabled
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Company Phone Number</label>
                <input
                  name="companyPhoneNumber"
                  value={form.companyPhoneNumber}
                  onChange={handleChange}
                  className="form-control"
                  disabled
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Company Email Address</label>
                <input
                  name="companyEmailAddress"
                  value={form.companyEmailAddress}
                  onChange={handleChange}
                  className="form-control"
                  type="email"
                  disabled
                />
              </div>
              <div className="col-12">
                <label className="form-label">Company Address</label>
                <textarea
                  name="companyAddress"
                  value={form.companyAddress}
                  onChange={handleChange}
                  className="form-control"
                  rows={3}
                  disabled
                />
              </div>
            </>
          ) : (
            <>
              <div className="col-12">
                <label className="form-label">Search Customer</label>
                <AutoComplete
                  value={searchValue}
                  options={options}
                  onSearch={handleSearch}
                  onSelect={handleSelect}
                  placeholder="Type customer name..."
                  className="w-100"
                  allowClear
                  notFoundContent={searchLoading ? <Spin size="small" /> : null}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Customer Name</label>
                <input
                  name="customerName"
                  value={form.customerName}
                  onChange={handleChange}
                  className="form-control"
                  disabled
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Customer ID</label>
                <input
                  name="customerID"
                  value={form.customerID}
                  onChange={handleChange}
                  className="form-control"
                  type="number"
                  disabled
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Phone Number</label>
                <input
                  name="phoneNumber"
                  value={form.phoneNumber}
                  onChange={handleChange}
                  className="form-control"
                  disabled
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Address</label>
                <input
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                  className="form-control"
                  disabled
                />
              </div>
            </>
          )}

          {/* Credit Terms Section */}
          <div className="col-12">
            <h6 className="border-bottom pb-2 mb-3 mt-3">Credit Terms</h6>
          </div>

          <div className="col-md-4">
            <label className="form-label">Credit Start Date</label>
            <DatePicker
              value={
                isEditing
                  ? form.creditStartDate
                    ? dayjs(form.creditStartDate)
                    : null
                  : dayjs()
              }
              className="form-control"
              format="YYYY-MM-DD"
              placeholder="Today"
              disabled
              inputReadOnly
            />
          </div>

          <div className="col-md-4">
            <label className="form-label">Payment Terms</label>
            <select
              name="paymentTerms"
              value={form.paymentTerms}
              onChange={handleChange}
              className="form-select"
              required
            >
              <option value="">Select payment terms</option>
              {/* Ensure existing non-standard term (e.g., "7 Days") appears */}
              {form.paymentTerms &&
                !["Net 7", "Net 14", "Net 30"].includes(form.paymentTerms) && (
                  <option value={form.paymentTerms}>{form.paymentTerms}</option>
                )}
              <option value="Net 7">Net 7 days</option>
              <option value="Net 14">Net 14 days</option>
              <option value="Net 30">Net 30 Days</option>
            </select>
          </div>

          <div className="col-md-4">
            <label className="form-label">Credit Limit</label>
            <div className="input-group">
              <span className="input-group-text">R</span>
              <input
                name="creditLimit"
                value={form.creditLimit}
                onChange={handleChange}
                className="form-control"
                type="number"
                min="0"
                step="0.01"
                required
              />
            </div>
          </div>

          {/* Credit Limit Warning Message */}
          {showPendingMessage && (
            <div className="col-12">
              <div
                className="alert alert-warning d-flex align-items-center"
                role="alert"
              >
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                <div>
                  <strong>Credit Limit Exceeded:</strong> Credit amounts above R
                  10,000 require approval. The exceed_Limit flag will be set to{" "}
                  <strong>"Yes"</strong> and this account will require
                  management review.
                </div>
              </div>
            </div>
          )}

          <div className="col-md-4">
            <label className="form-label">Payment Type</label>
            <input className="form-control" value="Account Payment" disabled />
          </div>

          <div className="col-12">
            <button
              className="btn btn-primary d-flex align-items-center gap-2"
              type="submit"
              disabled={
                submitting ||
                !(clientType && (form.companyID || form.customerID))
              }
            >
              {submitting && <Loader2 className="spin" size={16} />}
              <Plus size={16} />
              {submitting
                ? isEditing
                  ? "Saving..."
                  : "Creating..."
                : isEditing
                ? "Save Changes"
                : "Create Credit Account"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default AddAccount;
